
import { Prop, Vue } from 'vue-property-decorator';
import EventBusConstants from '@/constants/EventBusConstants';
import PlayerEmitter from '@/event/PlayerEmitter';
import { inject } from 'vue';
import ToolDisplayManager from './ToolDisplayManager';
import ToolManager from '@/controllers/tools/ToolManager';

const START_Y_POSITION = 85;
const START_X_POSITION = 5;

export default class FloatingTool extends Vue {
  private topPosition_: number = START_Y_POSITION;
  private leftPosition_: number = START_X_POSITION;
  private emitter: PlayerEmitter | undefined = inject('playerEmitter');
  private toolDisplayManager: ToolDisplayManager | undefined = inject<ToolDisplayManager>('toolDisplayManager');
  private toolManager: ToolManager | undefined = inject<ToolManager>('toolManager');
  private zIndex_ = 2;
  private yAdjustment = 0;

  @Prop({ default: '' }) title: string | undefined;
  @Prop() toolName: string | undefined = undefined;
  @Prop({ default: '' }) bottomText: string | undefined;
  @Prop({ default: 400 }) width: number | undefined;
  @Prop({ default: 400 }) height: number | undefined;
  @Prop({ default: true }) showFooter: boolean | undefined;

  private get topPosition(): number {
    return this.topPosition_;
  }

  private get leftPosition(): number {
    return this.leftPosition_;
  }

  private get toolHidden(): boolean {
    if (this.toolName) {
      const value = !this.toolManager!.toolIsEnabled(this.toolName);
      setTimeout(() => this.updateZIndex(value), 0);
      return value;
    }
    return true;
  }

  private get bottomMarginSize(): number {
    return this.showFooter ? 0 : 4;
  }

  set zIndex(value: number) {
    this.zIndex_ = value;
  }

  get zIndex(): number {
    return this.zIndex_;
  }

  private toolSelected(): void {
    this.updateZIndex(false);
  }

  private updateZIndex(hidden: boolean): void {
    this.toolDisplayManager!.updateTool(this, hidden);
  }

  mounted() {
    this.dragElement();
    this.zIndex_ = this.toolDisplayManager!.getZIndex(this);
    this.$el.querySelector('#close-button').addEventListener('click', () => this.closeWindow());
    this.$el.querySelector('#close-button').addEventListener('touchend', () => this.closeWindow());
    this.emitter!.on(EventBusConstants.SECTION_CHANGE, () => {
      this.handleSectionChange();
    });

    window.addEventListener('resize', this.windowSizeChanged);

    const element = document.getElementById('inner-container');
    if (element) {
      new ResizeObserver(this.handleAdjustment).observe(element);
    }
  }

  private handleAdjustment(): void {
    const toolContainerElement: Element | null = document.getElementById('tool-container');
    if (toolContainerElement) {
      this.yAdjustment = toolContainerElement.getBoundingClientRect().top;
    }
    this.windowSizeChanged();
  }

  private handleSectionChange(): void {
    this.$emit('sectionChange');
  }

  private closeWindow(): void {
    if (this.toolName) {
      this.toolManager!.toggleTool(this.toolName);
    }
  }

  private dragElement(): void {
    const top: HTMLInputElement = this.$el.querySelector('#top');

    if (top) {
      let positionX = 0;
      let positionY = 0;
      top.onmousedown = (e) => {
        e.preventDefault();
        positionX = e.clientX;
        positionY = e.clientY;
        document.onmouseup = () => {
          document.onmouseup = null;
          document.onmousemove = null;
        };

        document.onmousemove = (e) => {
          e = e || window.event;
          e.preventDefault();

          this.adjustPosition(this.leftPosition - (positionX - e.clientX), this.topPosition - (positionY - e.clientY));
          positionX = e.clientX;
          positionY = e.clientY;
        };
      };

      top.ontouchstart = (e) => {
        e.preventDefault();
        positionX = e.touches[0].clientX;
        positionY = e.touches[0].clientY;
        document.ontouchend = () => {
          document.ontouchend = null;
          document.ontouchmove = null;
        };
        document.ontouchmove = (e) => {
          e = e || window.event;
          e.preventDefault();

          this.adjustPosition(
            this.leftPosition - (positionX - e.touches[0].clientX),
            this.topPosition - (positionY - e.touches[0].clientY)
          );

          positionX = e.touches[0].clientX;
          positionY = e.touches[0].clientY;
        };
      };
    }
  }

  private adjustPosition(xPosition, yPosition) {
    const minimumXPosition = 0;
    const maximumXPosition = document.documentElement.clientWidth - this.width!;
    const minimumYPosition = -this.yAdjustment;
    const maximumYPosition = document.documentElement.clientHeight - this.height! - this.yAdjustment;
    if (xPosition < minimumXPosition) {
      xPosition = minimumXPosition;
    }
    if (xPosition > maximumXPosition) {
      xPosition = maximumXPosition;
    }

    if (yPosition < minimumYPosition) {
      yPosition = minimumYPosition;
    }
    if (yPosition > maximumYPosition) {
      yPosition = maximumYPosition;
    }

    this.leftPosition_ = xPosition;
    this.topPosition_ = yPosition;
  }

  private windowSizeChanged() {
    this.adjustPosition(this.leftPosition_, this.topPosition_);
  }
}
